/*  /frontend/src/pages/Register.js  (v2.0 – modernized UI)
    All logic identical to v1.
------------------------------------------------------------------ */
import '../custom.css'

import React, { useEffect, useState, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Spinner } from 'react-bootstrap'
import { CheckCircleFill, StarFill } from 'react-bootstrap-icons'
import { TIMEZONES } from '../utils/timezones'
import { BIO_HOST } from '../utils/nameClaim'
import {
  claimFlowFrom, inClaimFlow, claimPayload, carryQuery, readNameContext,
  saveClaimWelcome, goTo, STUDIO_PATH,
} from '../utils/onboarding'
import { signupSourceFrom, trackFunnel, clearFirstTouch } from '../utils/funnel'
import axios from 'axios'

const logoUrl = '/assets/logo/greatly-social-full.png'

const BENEFITS = [
  { icon:'fas fa-magic',        color:'#7c3aed', text:'AI-powered post & caption generator' },
  { icon:'fas fa-calendar-alt', color:'#2563eb', text:'Drag-and-drop content calendar' },
  { icon:'fas fa-globe',        color:'#0891b2', text:'Publish to 7 platforms at once' },
  { icon:'fas fa-clock',        color:'#16a34a', text:'Auto-schedule at the best times' },
]

const inputStyle = {
  width       : '100%',
  padding     : '11px 14px',
  borderRadius: 9,
  border      : '1.5px solid #e5e7eb',
  fontSize    : 14,
  color       : '#111827',
  outline     : 'none',
  background  : '#ffffff',
  transition  : 'border-color 0.15s',
  boxSizing   : 'border-box',
}

/* From the name generators (Growth round, T20–T24):
     /register?claim=<link-in-bio name>&from=<page>&kit=<AI profile kit id>
   plus the ♡ shortlist kept in this browser. All three go with the sign-up;
   the server claims the name, keeps the shortlist and attaches the kit.
   Arriving that way the page is the Signup.dc.html layout, and a finished
   sign-up signs straight in and opens AI Studio with the welcome bar
   (utils/onboarding.js). A plain /register is unchanged.

   Sign-up source (T25): a visitor whose first touch was a generator page
   (utils/funnel.js) also sends signupPage / signupSource, and the page
   reports signup_started and signup_completed for that page. */
export { claimFlowFrom }

export default function Register () {
  const [name,       setName]       = useState('')
  const [email,      setEmail]      = useState('')
  const [password,   setPassword]   = useState('')
  const [showPw,     setShowPw]     = useState(false)
  const [timezone,   setTimezone]   = useState(Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC')
  const [avatarFile, setAvatarFile] = useState(null)
  const [preview,    setPreview]    = useState(null)
  const [loading,    setLoading]    = useState(false)
  const [message,    setMessage]    = useState({ text:'', variant:'success' })
  const [search]                    = useState(() => (typeof window !== 'undefined' ? window.location.search : ''))
  const [flow]                      = useState(() => claimFlowFrom(search))
  const claimMode                   = inClaimFlow(flow)
  const [source]                    = useState(() => signupSourceFrom(flow.from))   // { page, source } | null

  const navigate     = useNavigate()
  const fileInputRef = useRef(null)
  const started      = useRef(false)
  const backendUrl   = process.env.REACT_APP_BACKEND_URL

  const api = axios.create({
    baseURL    : backendUrl,
    withCredentials: true,
    headers    : { 'Content-Type': 'application/json' },
  })

  useEffect(() => {
    if (!backendUrl) {
      console.warn('[Register] REACT_APP_BACKEND_URL is undefined.')
      setMessage({ text: 'App misconfiguration: backend URL is not set.', variant: 'danger' })
    }
  }, [backendUrl])

  useEffect(() => {
    if (!source || started.current) return
    started.current = true
    trackFunnel('signup_started', source.page)
  }, [source])

  const handleAvatarSelect = e => {
    const file = e.target.files[0]
    if (file) { setAvatarFile(file); setPreview(URL.createObjectURL(file)) }
  }

  const handleSubmit = async e => {
    e.preventDefault()
    setMessage({ text:'', variant:'success' })
    setLoading(true)

    const payload = {
      name    : (name     || '').trim(),
      email   : (email    || '').trim().toLowerCase(),
      password: (password || '').trim(),
      timezone: (timezone || 'UTC').trim() || 'UTC',
      ...claimPayload(flow),
      ...(source ? { signupPage: source.page, signupSource: source.source } : {}),
    }

    let newUser = null
    let leaving = false           // opening the dashboard: keep the button busy
    try {
      const registerRes = await api.post('/api/auth/register', payload)
      newUser = registerRes?.data?.user
      // The account exists either way; a name taken in the meantime comes with a message.
      const claimNote = registerRes?.data?.claim?.message
      if (!newUser || !newUser.id) throw new Error('Registration did not return user data.')
      if (source) {
        trackFunnel('signup_completed', source.page)
        clearFirstTouch()   // the source now lives on the account
      }

      // From the name generators: sign straight in and open AI Studio, which
      // shows the welcome bar (and any claim message) and the prefilled goal.
      if (claimMode) {
        let token = null
        try {
          const loginRes = await api.post('/api/auth/login', { email: payload.email, password: payload.password })
          token = loginRes?.data?.token || null
        } catch (loginErr) {
          console.warn('[Register] sign-in after sign-up failed', loginErr)
          setMessage({ text: `Account created! ${claimNote || ''} Log in to continue.`.replace(/\s+/g, ' '), variant: 'success' })
          setTimeout(() => navigate(`/login${carryQuery(search)}`), 2500)
          return
        }
        await saveClaimWelcome({
          flow,
          claimResult: registerRes?.data?.claim,
          fetchKit: async kitId => (await api.get(`/api/ai/profile-kit/${encodeURIComponent(kitId)}`, {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          })).data,
        })
        if (window.gtag) window.gtag('event', 'sign_up', { method: 'email_password' })
        setMessage({ text: 'Account created! Opening your dashboard…', variant: 'success' })
        leaving = true
        goTo(STUDIO_PATH)
        return
      }

      // /api/avatar needs a login, so sign in with the new credentials first.
      // The account already exists, so a failed avatar upload is not fatal.
      if (avatarFile) {
        try {
          const loginRes = await api.post('/api/auth/login', { email: payload.email, password: payload.password })
          const formData = new FormData()
          formData.append('userId', newUser.id)
          formData.append('avatar', avatarFile)
          await axios.post(`${backendUrl}/api/avatar`, formData, {
            withCredentials: true,
            headers        : {
              'Content-Type': 'multipart/form-data',
              ...(loginRes?.data?.token ? { Authorization: `Bearer ${loginRes.data.token}` } : {}),
            },
          })
        } catch (avatarErr) {
          console.warn('[Register] avatar upload skipped', avatarErr)
        }
      }

      if (window.gtag) window.gtag('event', 'sign_up', { method: 'email_password' })

      setMessage({ text: claimNote ? `Account created! ${claimNote}` : 'Account created! Redirecting…', variant: 'success' })
      setTimeout(() => navigate('/login'), claimNote ? 4000 : 1500)
    } catch (err) {
      console.error('[Register] error', err)
      setMessage({
        text   : err?.response?.data?.message || err?.message || 'Registration failed.',
        variant: 'danger',
      })
    } finally {
      if (!leaving) setLoading(false)
    }
  }

  /* ── From the name generators: Signup.dc.html ── */
  if (claimMode) {
    const others = flow.shortlist.filter(n => n.toLowerCase() !== flow.claim)
    const disabled = loading || !backendUrl
    const field = (label, props) => (
      <label style={{ display:'flex', flexDirection:'column', gap:6, fontWeight:600, fontSize:14, color:'#212529' }}>
        {label}
        <input required {...props}
          style={{ height:42, border:'1px solid #ced4da', borderRadius:6, padding:'0 12px', fontSize:16, fontWeight:400, color:'#212529', outline:'none', boxSizing:'border-box' }}
          onFocus={e => { e.target.style.borderColor = '#86b7fe' }} onBlur={e => { e.target.style.borderColor = '#ced4da' }} />
      </label>
    )
    return (
      <div style={{ minHeight:'100vh', background:'#f8f9fa', color:'#212529', padding:'48px clamp(16px, 5vw, 60px)', display:'flex', flexWrap:'wrap', gap:48, alignItems:'flex-start', justifyContent:'center', boxSizing:'border-box' }}>
        <form onSubmit={handleSubmit} data-testid='claim-flow'
          style={{ flex:'1 1 360px', maxWidth:520, background:'#ffffff', border:'1px solid #e5e7eb', borderRadius:14, padding:28, display:'flex', flexDirection:'column', gap:16, boxSizing:'border-box' }}>
          <Link to='/' aria-label='Greatly Social home'><img src={logoUrl} alt='Greatly Social' style={{ height:28 }} /></Link>
          <h1 style={{ margin:0, fontSize:28, fontWeight:700 }}>Create your free account</h1>
          {flow.claim && (
            <div style={{ display:'flex', alignItems:'center', gap:10, background:'#e8f5ee', border:'1px solid #a3cfbb', borderRadius:10, padding:'12px 14px' }}>
              <span aria-hidden='true' style={{ width:10, height:10, borderRadius:5, background:'#198754', flex:'none' }} />
              <span style={{ fontSize:15, color:'#0f5132' }}>
                We'll save <strong>{BIO_HOST}{flow.claim}</strong> for you as soon as you sign up.
              </span>
            </div>
          )}
          {message.text && (
            <div role={message.variant === 'success' ? 'status' : 'alert'} style={{
              padding:'11px 14px', borderRadius:9, fontSize:14, fontWeight:600,
              background: message.variant === 'success' ? '#dcfce7' : '#fef2f2',
              border    : `1px solid ${message.variant === 'success' ? '#bbf7d0' : '#fecaca'}`,
              color     : message.variant === 'success' ? '#15803d' : '#dc2626',
            }}>{message.text}</div>
          )}
          {field('Name', { type:'text', value:name, onChange:e => setName(e.target.value), placeholder:'Your name', autoComplete:'name' })}
          {field('Email', { type:'email', value:email, onChange:e => setEmail(e.target.value), placeholder:'you@example.com', autoComplete:'email' })}
          {field('Password', { type:'password', value:password, onChange:e => setPassword(e.target.value), placeholder:'At least 8 characters', autoComplete:'new-password' })}
          <button type='submit' disabled={disabled}
            style={{ height:46, border:0, borderRadius:8, background: disabled ? '#6ea8fe' : '#0d6efd', color:'#fff', fontSize:16, fontWeight:700, cursor: disabled ? 'not-allowed' : 'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:8 }}>
            {loading
              ? <><Spinner animation='border' size='sm' style={{ width:14, height:14 }} /> Creating account…</>
              : (flow.claim ? `Create account and claim ${flow.claim}` : 'Create Free Account')}
          </button>
          {flow.shortlist.length > 0 && (
            <div style={{ fontSize:14, color:'#495057' }}>
              Your shortlist comes with you:{' '}
              {flow.shortlist.map((n, i) => <React.Fragment key={n}>{i > 0 && ', '}<strong>{n}</strong></React.Fragment>)}.
              {flow.claim && others.length > 0 && (others.length === 1 ? ' You can switch to the other name later.' : ' You can switch to another name later.')}
            </div>
          )}
          {flow.kitId && (
            <div style={{ fontSize:14, color:'#495057' }}>
              Your AI profile kit comes with you too: every bio, your first-post captions and your hashtag set.
            </div>
          )}
          <div style={{ fontSize:14, color:'#495057' }}>
            Already have an account? <Link to={`/login${carryQuery(search)}`} style={{ color:'#0a58ca' }}>Log in</Link>
            {flow.claim ? " and we'll still save the name." : " and we'll still save your names."}
          </div>
          <p style={{ fontSize:12, color:'#6c757d', margin:0 }}>
            By registering you agree to our <a href='/terms' style={{ color:'#6c757d' }}>Terms of Service</a> and <a href='/privacy' style={{ color:'#6c757d' }}>Privacy Policy</a>.
          </p>
        </form>
        <ClaimBenefits name={flow.claim} />
      </div>
    )
  }

  return (
    <div style={{ minHeight:'100vh', display:'flex', fontFamily:"'DM Sans', system-ui, sans-serif" }}>

      {/* ── Left: Brand panel (hidden on mobile) ── */}
      <div
        className='d-none d-md-flex'
        style={{
          flex          : '1 1 420px',
          background    : 'linear-gradient(160deg, #0f172a 0%, #1e3a5f 60%, #1d4ed8 100%)',
          flexDirection : 'column',
          justifyContent: 'center',
          padding       : '60px 52px',
          position      : 'relative',
          overflow      : 'hidden',
        }}
      >
        {/* Blobs */}
        <div style={{ position:'absolute', top:-100, right:-100, width:360, height:360, borderRadius:'50%', background:'rgba(37,99,235,0.2)', filter:'blur(60px)', pointerEvents:'none' }} />
        <div style={{ position:'absolute', bottom:-80, left:-80, width:300, height:300, borderRadius:'50%', background:'rgba(124,58,237,0.15)', filter:'blur(50px)', pointerEvents:'none' }} />

        <div style={{ position:'relative', zIndex:1 }}>
          <img src={logoUrl} alt='Greatly Social' style={{ height:32, marginBottom:36, filter:'brightness(0) invert(1)' }} />

          <h2 style={{ fontSize:30, fontWeight:900, color:'#ffffff', lineHeight:1.2, marginBottom:14 }}>
            Everything you need<br />to win on social media.
          </h2>
          <p style={{ fontSize:15, color:'rgba(255,255,255,0.65)', lineHeight:1.7, marginBottom:32 }}>
            Join thousands of small businesses, realtors, and agencies who use Greatly Social to stay consistent — without the stress.
          </p>

          {/* Benefits list */}
          <div style={{ display:'flex', flexDirection:'column', gap:14, marginBottom:36 }}>
            {BENEFITS.map(b => (
              <div key={b.text} style={{ display:'flex', alignItems:'center', gap:12 }}>
                <div style={{ width:34, height:34, borderRadius:9, background:'rgba(255,255,255,0.1)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                  <i className={b.icon} style={{ fontSize:14, color:'#fff' }} />
                </div>
                <span style={{ fontSize:14, color:'rgba(255,255,255,0.8)', fontWeight:500 }}>{b.text}</span>
              </div>
            ))}
          </div>

          {/* Social proof */}
          <div style={{ background:'rgba(255,255,255,0.08)', border:'1px solid rgba(255,255,255,0.12)', borderRadius:14, padding:'16px 20px', display:'flex', alignItems:'center', gap:14 }}>
            <div style={{ textAlign:'center', flexShrink:0 }}>
              <div style={{ fontSize:22, fontWeight:900, color:'#60a5fa' }}>10K+</div>
              <div style={{ fontSize:11, color:'rgba(255,255,255,0.45)', fontWeight:500 }}>Businesses</div>
            </div>
            <div style={{ width:1, height:36, background:'rgba(255,255,255,0.1)' }} />
            <div style={{ textAlign:'center', flexShrink:0 }}>
              <div style={{ fontSize:22, fontWeight:900, color:'#60a5fa' }}>2M+</div>
              <div style={{ fontSize:11, color:'rgba(255,255,255,0.45)', fontWeight:500 }}>Posts Published</div>
            </div>
            <div style={{ width:1, height:36, background:'rgba(255,255,255,0.1)' }} />
            <div style={{ textAlign:'center', flexShrink:0 }}>
              <div style={{ fontSize:22, fontWeight:900, color:'#60a5fa' }}>Free</div>
              <div style={{ fontSize:11, color:'rgba(255,255,255,0.45)', fontWeight:500 }}>14-day trial</div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Right: Form ── */}
      <div style={{ flex:'1 1 480px', display:'flex', flexDirection:'column', justifyContent:'center', alignItems:'center', padding:'48px 32px', background:'#ffffff', overflowY:'auto' }}>
        <div style={{ width:'100%', maxWidth:440 }}>

          {/* Mobile logo */}
          <div className='d-md-none' style={{ marginBottom:28 }}>
            <img src={logoUrl} alt='Greatly Social' style={{ height:32 }} />
          </div>

          <h1 style={{ fontSize:24, fontWeight:900, color:'#111827', marginBottom:6 }}>Create your free account</h1>
          <p style={{ fontSize:14, color:'#6b7280', marginBottom:24 }}>
            Already have an account?{' '}
            <Link to='/login' style={{ color:'#2563eb', fontWeight:700, textDecoration:'none' }}>Log in</Link>
          </p>

          {/* Trust badges */}
          <div style={{ display:'flex', gap:16, marginBottom:24, flexWrap:'wrap' }}>
            {['No credit card required', 'Cancel anytime'].map(t => (
              <span key={t} style={{ display:'flex', alignItems:'center', gap:5, fontSize:12, color:'#6b7280' }}>
                <CheckCircleFill size={12} color='#16a34a' />{t}
              </span>
            ))}
          </div>

          {/* Alert */}
          {message.text && (
            <div style={{
              display     : 'flex',
              alignItems  : 'center',
              gap         : 8,
              padding     : '11px 14px',
              borderRadius: 9,
              marginBottom: 20,
              fontSize    : 13,
              fontWeight  : 600,
              background  : message.variant === 'success' ? '#dcfce7' : '#fef2f2',
              border      : `1px solid ${message.variant === 'success' ? '#bbf7d0' : '#fecaca'}`,
              color       : message.variant === 'success' ? '#16a34a' : '#dc2626',
            }}>
              {message.variant === 'success'
                ? <CheckCircleFill size={14} />
                : <i className='fas fa-exclamation-circle' style={{ fontSize:13 }} />
              }
              {message.text}
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit}>

            {/* Avatar picker */}
            <div style={{ display:'flex', alignItems:'center', gap:14, marginBottom:20, padding:'14px', background:'#f8fafc', borderRadius:10, border:'1px solid #e5e7eb' }}>
              <div onClick={() => fileInputRef.current?.click()} style={{ position:'relative', cursor:'pointer', flexShrink:0 }}>
                <img
                  src={preview || 'https://placehold.co/52x52/e5e7eb/9ca3af?text=?'}
                  alt='Avatar'
                  style={{ width:52, height:52, borderRadius:'50%', objectFit:'cover', border:'2px solid #e5e7eb' }}
                />
                <div style={{ position:'absolute', bottom:-2, right:-2, width:20, height:20, borderRadius:'50%', background:'#2563eb', border:'2px solid #fff', display:'flex', alignItems:'center', justifyContent:'center' }}>
                  <i className='fas fa-camera' style={{ fontSize:7, color:'#fff' }} />
                </div>
              </div>
              <div>
                <div style={{ fontSize:13, fontWeight:600, color:'#374151', marginBottom:2 }}>Profile photo <span style={{ color:'#9ca3af', fontWeight:400 }}>(optional)</span></div>
                <button type='button' onClick={() => fileInputRef.current?.click()} style={{ fontSize:12, color:'#2563eb', fontWeight:600, background:'transparent', border:'none', cursor:'pointer', padding:0 }}>
                  {preview ? 'Change photo' : 'Upload photo'}
                </button>
              </div>
              <input ref={fileInputRef} type='file' accept='image/*' style={{ display:'none' }} onChange={handleAvatarSelect} />
            </div>

            {/* Name */}
            <div style={{ marginBottom:14 }}>
              <label style={{ fontSize:13, fontWeight:600, color:'#374151', display:'block', marginBottom:6 }}>Full name</label>
              <input type='text' value={name} onChange={e => setName(e.target.value)} required placeholder='Jane Smith'
                style={inputStyle}
                onFocus={e => e.target.style.borderColor='#2563eb'} onBlur={e => e.target.style.borderColor='#e5e7eb'} />
            </div>

            {/* Email */}
            <div style={{ marginBottom:14 }}>
              <label style={{ fontSize:13, fontWeight:600, color:'#374151', display:'block', marginBottom:6 }}>Email address</label>
              <input type='email' value={email} onChange={e => setEmail(e.target.value)} required placeholder='you@company.com'
                style={inputStyle}
                onFocus={e => e.target.style.borderColor='#2563eb'} onBlur={e => e.target.style.borderColor='#e5e7eb'} />
            </div>

            {/* Password */}
            <div style={{ marginBottom:14 }}>
              <label style={{ fontSize:13, fontWeight:600, color:'#374151', display:'block', marginBottom:6 }}>Password</label>
              <div style={{ position:'relative' }}>
                <input type={showPw ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} required placeholder='Min. 8 characters'
                  style={{ ...inputStyle, paddingRight:50 }}
                  onFocus={e => e.target.style.borderColor='#2563eb'} onBlur={e => e.target.style.borderColor='#e5e7eb'} />
                <button type='button' onClick={() => setShowPw(p => !p)}
                  style={{ position:'absolute', right:12, top:'50%', transform:'translateY(-50%)', background:'transparent', border:'none', cursor:'pointer', fontSize:12, fontWeight:600, color:'#6b7280' }}>
                  {showPw ? 'Hide' : 'Show'}
                </button>
              </div>
            </div>

            {/* Timezone */}
            <div style={{ marginBottom:24 }}>
              <label style={{ fontSize:13, fontWeight:600, color:'#374151', display:'block', marginBottom:6 }}>
                Time zone <span style={{ color:'#9ca3af', fontWeight:400 }}>(for scheduling)</span>
              </label>
              <select value={timezone} onChange={e => setTimezone(e.target.value)}
                style={{ ...inputStyle, cursor:'pointer' }}
                onFocus={e => e.target.style.borderColor='#2563eb'} onBlur={e => e.target.style.borderColor='#e5e7eb'}>
                {TIMEZONES.map(tz => <option key={tz} value={tz}>{tz}</option>)}
              </select>
            </div>

            <button
              type='submit'
              disabled={loading || !backendUrl}
              style={{
                width          : '100%',
                padding        : '13px',
                borderRadius   : 10,
                border         : 'none',
                background     : (loading || !backendUrl) ? '#93c5fd' : '#2563eb',
                color          : '#ffffff',
                fontSize       : 15,
                fontWeight     : 700,
                cursor         : (loading || !backendUrl) ? 'not-allowed' : 'pointer',
                display        : 'flex',
                alignItems     : 'center',
                justifyContent : 'center',
                gap            : 8,
                boxShadow      : '0 4px 14px rgba(37,99,235,0.3)',
                transition     : 'background 0.15s',
                marginBottom   : 14,
              }}
              onMouseEnter={e => { if (!loading && backendUrl) e.currentTarget.style.background='#1d4ed8' }}
              onMouseLeave={e => { if (!loading && backendUrl) e.currentTarget.style.background='#2563eb' }}
            >
              {loading
                ? <><Spinner animation='border' size='sm' style={{ width:14, height:14 }} /> Creating account…</>
                : <><i className='fas fa-arrow-right' style={{ fontSize:12 }} /> Create Free Account</>
              }
            </button>

            <p style={{ fontSize:12, color:'#9ca3af', textAlign:'center', lineHeight:1.5 }}>
              By registering you agree to our{' '}
              <a href='/terms' style={{ color:'#6b7280', fontWeight:600 }}>Terms of Service</a>
              {' '}and{' '}
              <a href='/privacy' style={{ color:'#6b7280', fontWeight:600 }}>Privacy Policy</a>.
            </p>
          </form>

          {/* Stars */}
          <div style={{ marginTop:24, paddingTop:20, borderTop:'1px solid #f3f4f6', textAlign:'center' }}>
            <div style={{ display:'flex', justifyContent:'center', gap:3, marginBottom:5 }}>
              {[...Array(5)].map((_, i) => <StarFill key={i} size={12} color='#fbbf24' />)}
            </div>
            <p style={{ fontSize:12, color:'#9ca3af', margin:0 }}>
              Trusted by <strong style={{ color:'#374151' }}>10,000+</strong> businesses worldwide
            </p>
          </div>

        </div>
      </div>
    </div>
  )
}

/* "What you get, free" beside the claim-flow form (Signup.dc.html). */
function ClaimBenefits ({ name }) {
  const ctx = readNameContext()
  const feel = ctx && ctx.styleLabel ? ctx.styleLabel.toLowerCase() : ''
  const items = [
    ['Your link-in-bio page', `${name ? `${BIO_HOST}${name}` : 'Your own greatlysocial.com page'}, with your links, latest posts and a sign-up box for followers.`],
    ['Schedule your posts', 'Plan the first week of posts and let them go out on their own.'],
    ['AI captions in your style', feel ? `Captions that match the ${feel} feel of your new handle.` : 'Captions that match the feel of your new handle.'],
    ['Your saved names and bio', 'Everything you made with the free tools, kept in one place.'],
  ]
  return (
    <div style={{ flex:'1 1 320px', maxWidth:480, display:'flex', flexDirection:'column', gap:16, paddingTop:8 }}>
      <h2 style={{ margin:0, fontSize:22, fontWeight:700 }}>What you get, free</h2>
      {items.map(([title, body]) => (
        <div key={title} style={{ display:'flex', gap:10, alignItems:'flex-start' }}>
          <svg width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='#198754' strokeWidth='2.4' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true' style={{ flex:'none', marginTop:2 }}>
            <path d='M5 12l5 5 9-10' />
          </svg>
          <div>
            <div style={{ fontWeight:700 }}>{title}</div>
            <div style={{ fontSize:15, color:'#495057' }}>{body}</div>
          </div>
        </div>
      ))}
    </div>
  )
}